import type { Key } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { SpecialFolderTypeEnum } from '../../enums/SpecialFolderTypeEnum';
import type File from '../../interfaces/File';
import type FilesDragged from '../../interfaces/FilesDragged';
import {
  buildFilesDragged,
  hasFilesDragged,
  INTERNAL_DND_MIME,
  readFilesDragged,
} from '../../lib/filesDnd';
import { useAppStore } from '../../store/store';
import { hasFileLimitation } from '../../utils/utils';
import { getAntTableScrollElement } from '../useAntTableOverlayScrollbars';
import { useAuth } from '../useAuth';
import { useFileDownload } from '../useFileDownload';
import useFilesDragGhostCard from '../useFilesDragGhostCard';
import { useFileUpload } from '../useFileUpload';

const AUTO_SCROLL_ZONE_SIZE = 100;
const AUTO_SCROLL_MAX_SPEED = 20;
const AUTO_SCROLL_MIN_SPEED = 2;

export interface UseFilesTableParams {
  files: File[];
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  handleEmptyAreaContextMenu: (event: React.MouseEvent) => void;
  handleFileRowClick: (file: File, hasClickedOnFilename: boolean, event?: React.MouseEvent) => void;
  handleFileRowDoubleClick: (file: File) => void;
  handleRowContextMenu: (record: File, event: React.MouseEvent) => void;
  handleMoveDraggedFiles: (files: File[], parentFolder: File | null) => void;
}

export function useFilesTable({
  files,
  selectedTableKeys,
  setSelectedTableKeys,
  handleEmptyAreaContextMenu,
  handleFileRowClick,
  handleFileRowDoubleClick,
  handleRowContextMenu,
  handleMoveDraggedFiles,
}: UseFilesTableParams) {
  const { uploadFiles } = useFileUpload();
  const { handleDownloadFiles } = useFileDownload();
  const { show: showDragGhost, hide: hideDragGhost } = useFilesDragGhostCard();
  const [draggingRowId, setDraggingRowId] = useState<number | null>(null);
  const draggedFilesRef = useRef<File[]>([]);
  const droppedInternallyRef = useRef(false);
  const activeRowIdRef = useRef<number | null>(null);
  const activeRowElementRef = useRef<HTMLElement | null>(null);
  const rowElementsMapRef = useRef<Map<number, HTMLElement>>(new Map());
  const dragTypeRef = useRef<'internal' | 'external' | null>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);

  const setIsGlobalFileDropzoneDisabled = useAppStore((s) => s.setIsGlobalFilesDropzoneDisabled);

  const updateActiveRowHighlight = useCallback(
    (rowId: number | null, rowElement: HTMLElement | null) => {
      if (activeRowIdRef.current === rowId) return;

      if (activeRowElementRef.current) {
        activeRowElementRef.current.classList.remove('is-dragover');
      }

      activeRowIdRef.current = rowId;
      activeRowElementRef.current = rowElement;

      if (rowElement) {
        rowElement.classList.add('is-dragover');
      }
    },
    [],
  );

  const clearRow = useCallback((id: number | null) => {
    if (id == null) {
      if (activeRowElementRef.current) {
        activeRowElementRef.current.classList.remove('is-dragover');
      }
      activeRowIdRef.current = null;
      activeRowElementRef.current = null;
      return;
    }
    if (activeRowIdRef.current === id && activeRowElementRef.current) {
      activeRowElementRef.current.classList.remove('is-dragover');
      activeRowIdRef.current = null;
      activeRowElementRef.current = null;
    }
  }, []);

  const { isInFileRoute } = useAuth();

  const getRowElement = useCallback(
    (recordId: number, currentTarget: HTMLElement): HTMLElement | null => {
      const cached = rowElementsMapRef.current.get(recordId);
      if (cached && document.contains(cached)) {
        return cached;
      }

      const rowElement = currentTarget.closest('tr[data-row-key]') as HTMLElement;
      if (rowElement) {
        rowElementsMapRef.current.set(recordId, rowElement);
        return rowElement;
      }

      return null;
    },
    [],
  );

  const isDropDisabledInThisFile = (file: File) => {
    return (
      !file.isFolder ||
      file.isReadOnly ||
      isInFileRoute(FilesTableEnum.Trash) ||
      file.specialFolder === SpecialFolderTypeEnum.QuickShare
    );
  };

  const hasFilesType = (dataTransfer?: DataTransfer | null) => {
    const types = dataTransfer?.types;
    if (!types || types.length === 0) return false;
    for (let i = 0; i < types.length; i++) {
      if (types[i] === 'Files') {
        return true;
      }
    }
    return false;
  };

  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  const updateAutoScroll = useCallback(
    (mouseY: number, tableBody: HTMLElement) => {
      const rect = tableBody.getBoundingClientRect();
      const distanceFromTop = mouseY - rect.top;
      const distanceFromBottom = rect.bottom - mouseY;

      let scrollSpeed = 0;
      let direction: 'up' | 'down' | null = null;

      if (distanceFromTop > 0 && distanceFromTop < AUTO_SCROLL_ZONE_SIZE) {
        direction = 'up';
        const ratio = 1 - distanceFromTop / AUTO_SCROLL_ZONE_SIZE;
        scrollSpeed =
          AUTO_SCROLL_MIN_SPEED + (AUTO_SCROLL_MAX_SPEED - AUTO_SCROLL_MIN_SPEED) * ratio;
      } else if (distanceFromBottom > 0 && distanceFromBottom < AUTO_SCROLL_ZONE_SIZE) {
        direction = 'down';
        const ratio = 1 - distanceFromBottom / AUTO_SCROLL_ZONE_SIZE;
        scrollSpeed =
          AUTO_SCROLL_MIN_SPEED + (AUTO_SCROLL_MAX_SPEED - AUTO_SCROLL_MIN_SPEED) * ratio;
      }

      if (direction && scrollSpeed > 0) {
        stopAutoScroll();

        const scroll = () => {
          const scrollElement = getAntTableScrollElement(tableBody) ?? tableBody;
          const currentScrollTop = scrollElement.scrollTop;
          const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;

          if (direction === 'up' && currentScrollTop > 0) {
            scrollElement.scrollTop = Math.max(0, currentScrollTop - scrollSpeed);
          } else if (direction === 'down' && currentScrollTop < maxScroll) {
            scrollElement.scrollTop = Math.min(maxScroll, currentScrollTop + scrollSpeed);
          }

          if (autoScrollIntervalRef.current !== null) {
            autoScrollIntervalRef.current = requestAnimationFrame(scroll);
          }
        };

        autoScrollIntervalRef.current = requestAnimationFrame(scroll);
      } else {
        stopAutoScroll();
      }
    },
    [stopAutoScroll],
  );

  useEffect(() => {
    const body = document.querySelector<HTMLElement>('.ant-table-body');
    if (!body) return;

    const handleBodyDrop = (e: Event) => {
      const el = e.target as HTMLElement;
      if (!el.closest('tr[data-row-key]')) {
        clearRow(null);
      }
    };

    body.addEventListener('drop', handleBodyDrop);

    return () => {
      body.removeEventListener('drop', handleBodyDrop);
    };
  }, [clearRow]);

  useEffect(() => {
    const handleGlobalDrop = () => {
      droppedInternallyRef.current = true;
    };

    window.addEventListener('drop', handleGlobalDrop, { capture: true });

    return () => {
      window.removeEventListener('drop', handleGlobalDrop, true);
    };
  }, []);

  useEffect(() => {
    const unsubscribeOver = useAppStore.getState().events.on.globalDropzoneDragOver(() => {
      clearRow(null);
    });
    const unsubscribeLeave = useAppStore.getState().events.on.globalDropzoneDragLeave(() => {});
    return () => {
      unsubscribeOver();
      unsubscribeLeave();
    };
  }, [clearRow]);

  useEffect(() => {
    const body = document.querySelector<HTMLElement>('.ant-table-body');
    if (!body) return;

    const handleDragOver = (e: DragEvent) => {
      if (!dragTypeRef.current) return;

      updateAutoScroll(e.clientY, body);
    };

    const handleDragLeave = (e: DragEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (!relatedTarget || !body.contains(relatedTarget)) {
        stopAutoScroll();
      }
    };

    const handleDrop = () => {
      stopAutoScroll();
    };

    const handleDragEnd = () => {
      stopAutoScroll();
    };

    body.addEventListener('dragover', handleDragOver);
    body.addEventListener('dragleave', handleDragLeave);
    body.addEventListener('drop', handleDrop);
    body.addEventListener('dragend', handleDragEnd);

    return () => {
      body.removeEventListener('dragover', handleDragOver);
      body.removeEventListener('dragleave', handleDragLeave);
      body.removeEventListener('drop', handleDrop);
      body.removeEventListener('dragend', handleDragEnd);
      stopAutoScroll();
    };
  }, [updateAutoScroll, stopAutoScroll]);

  const isInvalidInternalDrop = useCallback((payload: FilesDragged | null, target: File) => {
    if (!payload) return true;
    if (!Array.isArray(payload.files) || payload.files.length === 0) return true;
    if (!target.isFolder) return true;
    if (payload.files.some((f) => f.id === target.id)) return true;
    return false;
  }, []);

  const handleTableContextMenu = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      const isTableRow = target.closest('.ant-table-row') || target.closest('.my-table-row');

      if (!isTableRow) {
        event.preventDefault();
        handleEmptyAreaContextMenu(event);
      }
    },
    [handleEmptyAreaContextMenu],
  );

  const rowClassName = useCallback(
    (record: File) =>
      ['my-table-row', draggingRowId === record.id ? 'is-dragging' : ''].filter(Boolean).join(' '),
    [draggingRowId],
  );

  const onRow = (record: File) => ({
    draggable: !hasFileLimitation(record.fileLimitations, FileLimitationEnum.DenyFileMove),
    onDragStart: (e: React.DragEvent) => {
      droppedInternallyRef.current = false;

      const selectedIdSet = new Set(
        (selectedTableKeys as Array<number | string>)
          .map((k) => (typeof k === 'number' ? k : Number(k)))
          .filter((n) => Number.isFinite(n)) as number[],
      );
      const anchorIsSelected = selectedIdSet.has(record.id);
      const draggedFiles = anchorIsSelected
        ? files.filter((f) => selectedIdSet.has(f.id))
        : [record];

      draggedFilesRef.current = draggedFiles;

      const payload = buildFilesDragged(draggedFiles, record.id);
      e.dataTransfer?.setData(INTERNAL_DND_MIME, JSON.stringify(payload));
      e.dataTransfer?.setData('text/plain', JSON.stringify(payload));
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';

      const el = showDragGhost(draggedFiles);
      if (e.dataTransfer && el) {
        try {
          e.dataTransfer.setDragImage(el, 12, 12);
        } catch {
          requestAnimationFrame(() => {
            try {
              e.dataTransfer?.setDragImage(el, 12, 12);
            } catch {
              /* ignore */
            }
          });
        }
      }
      (el as HTMLElement).offsetHeight;

      setIsGlobalFileDropzoneDisabled(true);

      setDraggingRowId(record.id);
    },
    onDragEnd: (e: React.DragEvent) => {
      const isOutsideWindow =
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight;

      if (!droppedInternallyRef.current && isOutsideWindow) {
        const filesToDownload = draggedFilesRef.current;
        const canDownload = !filesToDownload.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileDownload),
        );

        if (canDownload && filesToDownload.length > 0) {
          handleDownloadFiles(filesToDownload);
        }
      }

      draggedFilesRef.current = [];
      droppedInternallyRef.current = false;
      dragTypeRef.current = null;
      rowElementsMapRef.current.clear();
      clearRow(null);
      setIsGlobalFileDropzoneDisabled(false);
      hideDragGhost();
      setDraggingRowId(null);
      stopAutoScroll();
    },

    onDragEnter: (e: React.DragEvent) => {
      if (isDropDisabledInThisFile(record)) return;

      if (activeRowIdRef.current === record.id) return;

      if (dragTypeRef.current === null) {
        const nat = e.nativeEvent as DragEvent;
        const internal = hasFilesDragged(nat);
        if (!internal) {
          dragTypeRef.current = hasFilesType(e.dataTransfer) ? 'external' : null;
        } else {
          dragTypeRef.current = 'internal';
        }
      }

      if (dragTypeRef.current) {
        const rowElement = getRowElement(record.id, e.currentTarget as HTMLElement);
        if (rowElement) {
          updateActiveRowHighlight(record.id, rowElement);
        }
      }
    },
    onDragLeave: (e: React.DragEvent) => {
      if (isDropDisabledInThisFile(record)) return;

      const { clientX, clientY } = e;
      const relatedTarget = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      const currentRow = (e.currentTarget as HTMLElement).closest('tr[data-row-key]');
      const targetRow = relatedTarget?.closest('tr[data-row-key]');

      if (currentRow && targetRow !== currentRow) {
        clearRow(record.id);
      }
    },
    onDragOver: (e: React.DragEvent) => {
      const nat = e.nativeEvent as DragEvent;
      const isInternalDrag = hasFilesDragged(nat);
      const dropDisabled = isDropDisabledInThisFile(record);
      const hasExternalFiles = !isInternalDrag && hasFilesType(e.dataTransfer);

      if (!dragTypeRef.current) {
        dragTypeRef.current = isInternalDrag ? 'internal' : hasExternalFiles ? 'external' : null;
      }

      if (dropDisabled && hasExternalFiles) {
        return;
      }

      if (!isInternalDrag && !hasExternalFiles) {
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = dragTypeRef.current === 'internal' ? 'move' : 'copy';
      }
    },
    onDrop: async (e: React.DragEvent) => {
      const nat = e.nativeEvent as DragEvent;
      const isInternalDrag = hasFilesDragged(nat);
      const hasExternalFiles = !isInternalDrag && hasFilesType(e.dataTransfer);
      const dropDisabled = isDropDisabledInThisFile(record);

      if (dropDisabled && hasExternalFiles) {
        return;
      }

      if (isInternalDrag) {
        droppedInternallyRef.current = true;
      }

      if (isInternalDrag || !dropDisabled) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (dropDisabled) {
        clearRow(record.id);
        setDraggingRowId(null);
        setIsGlobalFileDropzoneDisabled(false);
        stopAutoScroll();
        return;
      }

      try {
        if (isInternalDrag) {
          const payload = readFilesDragged(nat);
          if (isInvalidInternalDrop(payload, record)) return;

          handleMoveDraggedFiles(payload?.files ?? [], record);
        } else if (hasExternalFiles) {
          uploadFiles(
            e.dataTransfer!.items,
            false,
            record.id,
            undefined,
            record.isPasswordSet,
            record.fileName,
          );
        }
      } finally {
        clearRow(record.id);
        setDraggingRowId(null);
        setIsGlobalFileDropzoneDisabled(false);
        stopAutoScroll();
      }
    },

    onClick: (event: React.MouseEvent) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.classList.contains('ant-table-selection-column')
      ) {
        setSelectedTableKeys(
          selectedTableKeys.includes(record.id)
            ? selectedTableKeys.filter((key) => key !== record.id)
            : [...selectedTableKeys, record.id],
        );
        return;
      }

      const clickedOnFilename = Boolean(
        (event.target as HTMLElement).closest('.filename-click-handler'),
      );
      handleFileRowClick(record, clickedOnFilename, event);
    },
    onDoubleClick: (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleFileRowDoubleClick(record);
    },
    onContextMenu: (event: React.MouseEvent) => handleRowContextMenu(record, event),
  });

  return {
    handleTableContextMenu,
    rowClassName,
    onRow,
  };
}
